import React, { useEffect, useState } from "react";
import TopNavbar from "../../components/AdminPortal/TopNavbar";
import { useSidebar } from "../../context/SidebarContext";
import Details from "../../components/AdminPortal/CRM-approval/Details";
import EvaluationScoreBoard from "../../components/AdminPortal/CRM-approval/EvaluationScoreBoard";
import PersonalDetails from "../../components/AdminPortal/CRM-approval/PersonalDetails";
import FinancialDetails from "../../components/AdminPortal/CRM-approval/FinancialDetails";
import SubmittedDocuments from "../../components/AdminPortal/CRM-approval/SubmittedDocuments";
import LoanDetails from "../../components/AdminPortal/CRM-approval/LoanDetails";
import ReviewActions from "../../components/AdminPortal/CRM-approval/ReviewActions";
import { useParams, useNavigate } from "react-router-dom";
import { crmAPI } from "../../services/AdminPortal/crmAPI";
import ApplicationStatus from "../../components/StaffPortal/ApplicationStatus";
import AnalyzerFlow from "../AdminPortal/AnalyzerFlow";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";

const LoanApplicationView = () => {
  const { sidebarExpanded, isMobile } = useSidebar();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [application, setApplication] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusMessageType, setStatusMessageType] = useState(null); // 'success' or 'error'
  const [activeTab, setActiveTab] = useState("LoanApplications");

  // console.log("this is staffId", application);
  useEffect(() => {
    const getDocumnetsById = async () => {
      setLoading(true);
      try {
        // Single API call that includes populated documents
        const documentsData = await crmAPI.getLeadDocuments(id);
        console.log("documentsData", documentsData);
        if (!documentsData) {
          throw new Error("Documents not found");
        }

        setDocuments(documentsData);
      } catch (error) {
        console.error("Error fetching documents:", error);
        setError({
          message: error.message || "Failed to load documents",
          details: error.response?.data || null,
        });
        toast.error(error.message || "Failed to load documents");
      } finally {
        setLoading(false);
      }
    };

    const getLeadDetailsById = async () => {
      setLoading(true);
      try {
        // Single API call that includes populated documents
        const leadData = await crmAPI.getLeadById(id);

        if (!leadData) {
          throw new Error("Lead not found");
        }

        setApplication({
          ...leadData,
          // If you need to transform the documents data:
        });

        // console.log(application, "applicaitronoiasdl");
      } catch (error) {
        console.error("Error fetching lead details:", error);
        setError({
          message: error.message || "Failed to load lead details",
          details: error.response?.data || null,
        });
        toast.error(error.message || "Failed to load lead details");
      } finally {
        setLoading(false);
      }
    };

    getLeadDetailsById();
    getDocumnetsById();
  }, [id]);

  // New effect to check if all required documents are uploaded and verified
  useEffect(() => {
    const checkAndUpdateStatus = async () => {
      if (!application || !documents) return;

      // Check required documents presence and verified status
      const requiredTypes = {
        salaried: [
          "PAN_CARD",
          "AADHAR_CARD",
          "BANK_STATEMENT",
          "SALARY_SLIP",
          "LIVE_SELFIE",
          "CANCELED_CHEQUE",
        ],
        "self-employed": [
          "PAN_CARD",
          "AADHAR_CARD",
          "BANK_STATEMENT",
          "INCOME_TAX_RETURN",
          "LIVE_SELFIE",
          "CANCELED_CHEQUE",
        ],
        "business-owner": [
          "PAN_CARD",
          "AADHAR_CARD",
          "BANK_STATEMENT",
          "GST_DOCUMENT",
          "COMPANY_REGISTRATION_LETTER",
          "MSME_UDYAM_LICENSE",
          "MUNICIPAL_LICENSE",
          "RENTAL_AGREEMENT",
          "LIVE_SELFIE",
          "CANCELED_CHEQUE",
        ],
        default: [
          "PAN_CARD",
          "AADHAR_CARD",
          "BANK_STATEMENT",
          "SALARY_SLIP",
          "LIVE_SELFIE",
          "CANCELED_CHEQUE",
        ],
      };
      const employmentType = application?.user?.employmentType || "salaried";
      const requiredDocs =
        requiredTypes[employmentType] || requiredTypes.default;

      const missingDocs = requiredDocs.filter(
        (docType) => !documents.some((doc) => doc.documentType === docType)
      );

      const pendingDocs = documents.filter(
        (doc) => doc.status === "pending" || "reuploaded"
      );
      const incompleteDocs = documents.filter(
        (doc) => doc.status === "not-uploaded"
      );
      const rejectedDocs = documents.filter((doc) => doc.status === "rejected");
      const allDocumentsVerified = requiredDocs.every((type) => {
        const doc = documents.find(
          (d) => d.documentType === type && d.status === "verified"
        );
        return !!doc;
      });

      if (allDocumentsVerified && application.status === "new-leads") {
        try {
          setLoading(true);
          const newStatus = "submitted";
          const response = await crmAPI.updateLoanStatus(id, newStatus);
          console.log("Status auto-updated to submitted", response);
          setApplication((prev) => ({
            ...prev,
            status: newStatus,
            submittedAt: new Date().toISOString(),
          }));
          setStatusMessage(
            "All required documents uploaded and verified. Application status updated to submitted."
          );
          setStatusMessageType("success");
        } catch (error) {
          console.error("Error auto-updating status:", error);
          setStatusMessage("Failed to auto-update application status.");
          setStatusMessageType("error");
        } finally {
          setLoading(false);
        }
      }
    };

    checkAndUpdateStatus();
  }, [documents, application, id]);

  const layoutStyles = {
    marginLeft: isMobile ? "0" : sidebarExpanded ? "200px" : "60px",
    width: isMobile
      ? "100%"
      : `calc(100% - ${sidebarExpanded ? "200px" : "60px"})`,
  };

  const handleLoanStatusChange = async (newStatus, rejectionReason = null) => {
    // console.log('🚨 handleLoanStatusChange ENTRY - arguments:', arguments);
    // console.log('🚨 handleLoanStatusChange called with:', { newStatus, rejectionReason, id });
    setLoading(true);
    try {
      const response = await crmAPI.updateLoanStatus(id, newStatus, rejectionReason);
      // console.log("Lead status updated", response);

      setApplication((prevApplication) => ({
        ...prevApplication,
        status: newStatus,
      }));

      setStatusMessageType("success");
    } catch (error) {
      console.error("Error updating loan status:", error);
      setError({
        message: error.message || "Failed to update loan status",
        details: error.response?.data || null,
      });
      setStatusMessage("Failed to send the application to the Credit Manager.");
      setStatusMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  // Show error state
  if (error && !application) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl text-red-500 mb-4">Error: {error.message}</h2>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#FFCA20] text-black px-4 py-2 rounded"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-black text-white   mt-12"
      style={layoutStyles}
    >
      <TopNavbar />
      <div className="p-4">
      
<div className="mb-4 flex flex-wrap items-center justify-start gap-4">
          <h1 className="text-[#FFCA20] font-bold text-md hover:underline">
            <span
              className="text-[#FFCA20] cursor-pointer"
              onClick={() => {navigate("/staff/customers")}}
            >
              Loan Applications
            </span>
          </h1>
          <span>{'>'}</span>
          <h2 className="text-white font-medium text-md">
            Application Review
          </h2>
        </div>
        {/* universal analyser banner */}
        <div className="bg-[#208FFF33] p-3 mb-4 rounded-lg border border-[#333333]">
          <div className="flex flex-col sm:flex-row justify-between items-center  ">
            <div className="flex-1">
              <h4 className="text-white text-base font-medium">
                Review and analyse the submitted loan application with detailed
                insights.
              </h4>
            </div>
            <button
              onClick={() => {
                navigate(`/staff/universal-analysers/${id}`);
              }}
              className=" text-[#57ABFF] rounded-lg flex items-center gap-2 transition-colors"
            >
              <span>Run Universal Analyser</span>
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="w-full mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Left Section */}
            <div className="lg:col-span-2 space-y-4">
              <Details application={application} />
             <EvaluationScoreBoard loanId={id} application={application} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <PersonalDetails application={application} />
                <FinancialDetails loanId={id} application={application} />
              </div>
              <SubmittedDocuments
                application={application}
                setDocuments={setDocuments}
                leadId={id}
                documents={documents}
              />
            </div>

            {/* Right Section */}
            <div className="lg:col-span-1 space-y-4">
              <ApplicationStatus application={application} />
              {/* analyzer flow component */}
              <AnalyzerFlow loanId={id} />
              <LoanDetails application={application} />
            </div>

            <div className="lg:col-span-3 relative space-y-2 ">
              {!["processing", "rejected","approved"].includes(application?.status) && (
                <ReviewActions
                  application={application}
                  handleLoanStatusChange={handleLoanStatusChange}
                  documents={documents}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanApplicationView;
